<template>
    <b-modal id="company-detail-modal"
             :title="modalTitle"
             hide-footer
             size="xl"
             modal-class="default-modal"
             :hide-header-close="saving"
             @hide="onHide"
    >
        <div class="action-btns">
            <template v-if="mode === 'readOnly'">
                <div style="height: 32px"/>
            </template>
            <!-- [수정]과 [저장](submit)은 같은 자리라 key 로 구분한다(같은 DOM 이면 [수정] 클릭이 곧바로 submit 이 됐다) -->
            <template v-else-if="mode === 'detail'">
                <b-button key="edit"
                          variant="outline-primary"
                          :disabled="!detailData.company_id"
                          @click="changeMode('edit')"
                >
                    {{ $t('수정')}}
                </b-button>
            </template>
            <template v-else-if="mode === 'add'">
                <b-button variant="outline-primary"
                          form="company-form"
                          type="submit"
                          :disabled="saving"
                >
                    <b-spinner v-if="saving" small class="mr-1"/>
                    {{ $t('저장')}}
                </b-button>
            </template>
            <template v-else-if="mode === 'edit'">
                <b-button key="save"
                          variant="outline-primary"
                          form="company-form"
                          type="submit"
                          :disabled="saving"
                >
                    <b-spinner v-if="saving" small class="mr-1"/>
                    {{ $t('저장') }}
                </b-button>
                <b-button variant="outline-secondary"
                          :disabled="saving"
                          @click="cancelEdit"
                >
                    {{ $t('취소') }}
                </b-button>
            </template>
        </div>
        <div class="tabs-container">
            <b-tabs class="default-tabs" :class="{'with-actions': mode !== 'add'}" content-class="mt-3">
                <b-tab :title="$t('기본 정보')">
                    <template v-if="loading">
                        <b-skeleton-img no-aspect class="empty-content"></b-skeleton-img>
                    </template>
                    <PageEmptyComponent v-else-if="loadError && mode !== 'add'" compact :error="loadError" @retry="fetchDetailData"/>
                    <template v-else>
                        <CompanyDetail v-if="mode === 'detail'" :detail-data="detailData"/>
                        <CompanyForm v-else ref="companyForm" :mode="mode" :detail-data="detailData"
                                     @updateDetailData="updateDetailData"
                                     @saving="v => saving = v"
                                     @dirty="v => formDirty = v"
                        />
                    </template>
                </b-tab>
                <b-tab v-if="(mode === 'detail' || readOnly) && !loadError" :title="$t('소속 유저')">
                    <template v-if="loading">
                        <b-skeleton-img no-aspect class="empty-content"></b-skeleton-img>
                    </template>
                    <template v-else>
                        <CompanyMembers :members="detailData.member"
                                        :company="detailData"
                                        :readOnly="readOnly"
                                        @updateMember="updateMember"
                        />
                    </template>
                </b-tab>
                <b-tab v-if="(mode === 'detail' || readOnly) && !loadError" :title="$t('평가 참여 정보')">
                    <template v-if="loading">
                        <b-skeleton-img no-aspect class="empty-content"></b-skeleton-img>
                    </template>
                    <template v-else>
                        <AdminCompanyAssessmentInfo :selected_company="detailData"
                                                    :readOnly="readOnly"
                                                    
                        />
                    </template>
                </b-tab>
            </b-tabs>
        </div>
    </b-modal>
</template>

<script>
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import {getErrorMessage} from '@core/utils/api/errorMessage';
import CompanyDetail from '@shared/components/admin/pages/Management/Company/_SubComponent/CompanyDetail.vue';
import CompanyForm from '@shared/components/admin/pages/Management/Company/_SubComponent/CompanyForm.vue';
import CompanyMembers from '@shared/components/admin/pages/Management/Company/_SubComponent/CompanyMembers.vue';
import AdminCompanyAssessmentInfo
    from '@shared/components/admin/pages/Management/Company/_SubComponent/AssessmentInfo.vue';
import unsavedChangesGuard from '@shared/mixin/unsaved-changes-guard';

export default {
    name: 'CompanyModal',
    components: {
        PageEmptyComponent,
        AdminCompanyAssessmentInfo,
        CompanyMembers,
        CompanyForm,
        CompanyDetail,
    },
    mixins: [unsavedChangesGuard],
    props: {
        detailId: {
            type: [Number, String, null],
            default: null,
        }
    },
    data() {
        return {
            mode: 'detail', // detail | add | edit | readOnly
            detailData: {id: null},
            loading: false,
            loadError: '',
            saving: false,
            formDirty: false,
        };
    },
    computed: {
        hasUnsavedChanges() {
            return (this.mode === 'add' || this.mode === 'edit') && this.formDirty;
        },
        modalTitle() {
            if (this.mode === 'add') {
                return this.$t('기업 등록');
            } else if (this.mode === 'edit') {
                return this.$t('기업 수정');
            } else {
                return this.$t('기업 상세');
            }
        },
        readOnly(){
            return this.mode === 'readOnly';
        }
    },
    watch: {
        detailId: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.fetchDetailData();
                } else {
                    this.loadError = '';
                    this.detailData = {id: null};
                }
            }
        }
    },
    methods: {
        changeMode(mode) {
            if (mode === 'add') {
                this.detailData = {id: null};
            }
            this.mode = mode;
        },
        async cancelEdit() {
            if (!(await this.confirmDiscardChanges())) return;
            this.changeMode('detail');
        },
        // X·ESC·바깥 클릭·hide() 모두 이리로 온다. 저장 중이면 막고, 미저장 편집이 있으면 확인 후 닫는다
        onHide(bvEvent) {
            if (this.saving) {
                bvEvent.preventDefault();
                return;
            }
            if (this.hasUnsavedChanges) {
                bvEvent.preventDefault();
                this.confirmDiscardChanges().then((ok) => {
                    if (!ok) return;
                    this.formDirty = false;
                    this.$bvModal.hide('company-detail-modal');
                });
                return;
            }
            this.mode = 'detail';
        },
        updateDetailData(detailData) {
            this.$emit('updateDetailData', detailData);
            // 저장이 끝났으므로 닫기·상세 전환에 확인을 묻지 않는다.
            // CompanyForm 의 saving=false 신호는 이 호출 뒤에 오므로 여기서 먼저 풀어야 onHide 가 닫기를 막지 않는다
            this.saving = false;
            this.formDirty = false;
            if(this.mode === 'add'){
                this.$bvModal.hide('company-detail-modal');
            }
            else if(this.mode === 'edit'){
                // 상세 화면에 저장한 값을 반영한다 (전에는 모달을 다시 열 때까지 저장 전 값이 보였다)
                this.detailData = {...this.detailData, ...detailData};
                this.changeMode('detail');
            }
        },
        async fetchDetailData(){
            try {
                this.loading = true;
                
                const _res_company_info = await this.$axios.get(`/admin/company/${this.detailId}/info`);
                const {company, member, projects} = _res_company_info.data;
                this.loadError = '';
                this.detailData = {
                    ...company, company_id: this.detailId,
                    member: member.map((item,index) => ({...item, index})), projects
                };
            }
            catch (e) {
                console.error(e);
                this.detailData = {id: null};
                this.loadError = e.response && e.response.status === 404
                    ? '존재하지 않는 기업입니다.'
                    : getErrorMessage(e, '기업 정보를 불러오지 못했습니다.');
            }
            finally {
                this.loading = false;   
            }
        },
        updateMember(updatedUser){
            const idx = this.detailData.member.findIndex((item) => {
                return item.user_id === updatedUser.id;
            });
            this.$set(this.detailData.member, idx, updatedUser);
        }
    },
};
</script>

<style scoped lang="scss">

</style>
